
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Repeat, ChevronDown } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { TokenIcon } from '../../ui/ChainIcons';

const QuickPriceWidget = ({ symbol, price, change }: any) => (
    <div className="flex items-center justify-between p-3 rounded-sm bg-stone-50 dark:bg-white/[0.03] border border-stone-100 dark:border-white/5 group hover:border-nobel-gold/30 transition-all cursor-pointer">
        <div className="flex items-center gap-3">
            <TokenIcon symbol={symbol} className="w-7 h-7" />
            <div>
                <div className="text-[11px] font-black text-stone-900 dark:text-white leading-none uppercase tracking-widest">{symbol}</div>
                <div className="text-[10px] font-mono text-stone-500 mt-1">${price?.toFixed(2) || '0.00'}</div>
            </div>
        </div>
        <div className={`text-[10px] font-mono font-bold ${change >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
            {change >= 0 ? '+' : ''}{change?.toFixed(1) || '0.0'}%
        </div>
    </div>
);

interface SwapWidgetProps {
    tokenMap: Map<string, any>;
}

export const SwapWidget: React.FC<SwapWidgetProps> = ({ tokenMap }) => {
    return (
        <Card className="p-6 border-stone-200 dark:border-white/5 rounded-sm bg-white dark:bg-[#15171C] shadow-sm relative overflow-hidden group">
            <div className="flex items-center justify-between mb-8 border-b border-stone-100 dark:border-white/5 pb-4 shrink-0">
                <h3 className="font-serif text-xl font-bold text-[#0A0B0F] dark:text-white flex items-center gap-3 uppercase tracking-tight">
                    <Repeat size={18} className="text-nobel-gold" /> Atomic Swap
                </h3>
                <div className="text-[9px] font-black text-stone-400 uppercase tracking-widest">Router: GhostRelay</div>
            </div>

            <div className="space-y-2 mb-8">
                {tokenMap.get('SOL') && <QuickPriceWidget symbol="SOL" price={tokenMap.get('SOL')?.priceUsd} change={tokenMap.get('SOL')?.change24h} />}
                {tokenMap.get('SUI') && <QuickPriceWidget symbol="SUI" price={tokenMap.get('SUI')?.priceUsd} change={tokenMap.get('SUI')?.change24h} />}
            </div>

            <div className="space-y-6">
                <div className="bg-stone-50 dark:bg-black/20 p-5 rounded-sm border border-stone-100 dark:border-white/5">
                    <div className="flex justify-between text-[9px] font-black text-stone-400 uppercase tracking-widest mb-2.5">
                        <span>Sell From Vault</span>
                        <span>Bal: {tokenMap.get('SOL')?.balance.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 bg-white dark:bg-black/40 px-2 py-1 rounded-sm border border-stone-200 dark:border-white/10 shrink-0">
                            <TokenIcon symbol="SOL" className="w-6 h-6" />
                            <ChevronDown size={14} className="text-stone-400" />
                        </div>
                        <input type="number" placeholder="0.00" className="bg-transparent border-none outline-none font-mono text-2xl font-bold flex-1 text-[#0A0B0F] dark:text-white" />
                    </div>
                </div>

                <div className="flex justify-center -my-9 relative z-10">
                    <div className="p-3 bg-white dark:bg-[#1C1E26] border border-stone-100 dark:border-white/10 rounded-sm shadow-xl text-stone-400 group-hover:rotate-180 transition-transform duration-700 cursor-pointer">
                        <Repeat size={16} className="text-nobel-gold" />
                    </div>
                </div>

                <div className="bg-stone-50 dark:bg-black/20 p-5 rounded-sm border border-stone-100 dark:border-white/5">
                    <div className="text-[9px] font-black text-stone-400 uppercase tracking-widest mb-2.5">Buy Into Vault</div>
                    <div className="flex items-center gap-4">
                         <div className="flex items-center gap-2 bg-white dark:bg-black/40 px-2 py-1 rounded-sm border border-stone-200 dark:border-white/10 shrink-0">
                            <TokenIcon symbol="USDC" className="w-6 h-6" />
                            <ChevronDown size={14} className="text-stone-400" />
                        </div>
                        <div className="text-2xl font-mono font-bold text-stone-300 dark:text-stone-700">0.00</div>
                    </div>
                </div>
                
                <Button variant="primary" size="md" className="w-full h-16 font-black tracking-[0.25em] text-[11px] shadow-2xl rounded-sm">INITIATE ATOMIC SWAP</Button>
            </div>
            
            <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-nobel-gold opacity-[0.02] blur-3xl pointer-events-none" />
        </Card>
    );
};
